import { useCallback, useMemo, useState } from 'react';

import {
  StoreItem,
  TicketRequest,
  TicketsType,
  storeKeys,
  useCreateTicket,
  useShowCampaignSettings,
  useUpdateTicket,
} from '@givebacks/api/store';
import { useFormyValidator } from '@givebacks/formy';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError, isAxiosError } from 'core';
import { getErrorMessage_v2 } from 'core/Utils';
import { Alert, Box, ButtonMantine, Group, Modal, notifications } from 'ui';
import { UseFormReturnType } from 'ui/Form';

import { AddEditTicketContent } from './AddEditTicketForm';
import { AddEditTicketFormType } from './AddTicketForm.type';
import { DeleteTicketModal } from './DeleteTicketModal';

type AddEditTicketForms = {
  addEditTicketForm: UseFormReturnType<AddEditTicketFormType>;
  options: UseFormReturnType<{ options: StoreItem['options'] }>;
};

const AddEditTicketModalFooter = ({
  ticketId,
  onClose,
  setIsUniqueError,
  storeItemId,
  causeId,
  isDuplicate,
  isSeasonTicket,
}: {
  ticketId?: string;
  onClose: () => void;
  setIsUniqueError: (value: boolean) => void;
  storeItemId: string;
  causeId: string;
  isDuplicate?: boolean;
  isSeasonTicket?: boolean;
}) => {
  const queryClient = useQueryClient();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const ticketsType = isSeasonTicket ? 'season_passes' : 'tickets';

  const { mutate: createTicket, isPending: isCreateTicketLoading } = useCreateTicket({
    ticketsType,
  });
  const { mutate: updateTicket, isPending: isUpdateTicketLoading } = useUpdateTicket({
    ticketsType,
  });
  const { data: campaignSettings } = useShowCampaignSettings({
    campaignId: storeItemId,
    causeId,
  });

  const { useGetForm, validateForms } = useFormyValidator<AddEditTicketForms>();
  const addEditTicketForm = useGetForm('addEditTicketForm');
  const options = useGetForm('options')?.getTransformedValues();

  const handleCreate = useCallback(
    (ticket: TicketRequest<TicketsType>) => {
      createTicket(
        {
          eventId: storeItemId,
          causeId,
          ticket,
        },
        {
          onSuccess: () => {
            // Invalidate event dates query to include the new ticket type
            void queryClient.invalidateQueries({
              queryKey: storeKeys.id(causeId).items.events.id(storeItemId).dates.all,
            });
            notifications.show({
              message: 'Ticket Type Created',
              variant: 'success',
            });
            onClose();
          },
          onError: (e) => {
            const error = e as Error | AxiosError;
            if (
              isAxiosError(error) &&
              error?.response?.data.error.includes('already') &&
              error?.response?.data.attributes.includes('name')
            ) {
              setIsUniqueError(true);
            } else {
              notifications.show({
                message: getErrorMessage_v2(error, 'Failed to create ticket type'),
                variant: 'error',
              });
            }
          },
        }
      );
    },
    [createTicket, storeItemId, causeId, queryClient, onClose, setIsUniqueError]
  );

  const key = useMemo(() => {
    if (isSeasonTicket) {
      return storeKeys.id(causeId).items.events.id(storeItemId).seasonPasses.all;
    } else {
      return storeKeys.id(causeId).items.events.id(storeItemId).tickets.all;
    }
  }, [causeId, isSeasonTicket, storeItemId]);

  const handleUpdate = useCallback(
    (ticket: TicketRequest<TicketsType>) => {
      if (!ticketId) return;
      updateTicket(
        {
          eventId: storeItemId,
          causeId,
          ticket,
          ticketId,
        },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey: key,
            });
            notifications.show({
              message: 'Ticket Type Updated',
              variant: 'success',
            });
            onClose();
          },
          onError: (e) => {
            const error = e as Error | AxiosError;
            if (
              isAxiosError(error) &&
              error?.response?.data.error.includes('already') &&
              error?.response?.data.attributes.includes('name')
            ) {
              setIsUniqueError(true);
            } else {
              notifications.show({
                message: getErrorMessage_v2(error, 'Failed to update ticket type'),
                variant: 'error',
              });
            }
          },
        }
      );
    },
    [ticketId, updateTicket, storeItemId, causeId, queryClient, key, onClose, setIsUniqueError]
  );

  const handleSave = useCallback(() => {
    setIsUniqueError(false);

    const ticket: TicketRequest<TicketsType> = {
      name: addEditTicketForm?.values?.name,
      price:
        addEditTicketForm?.values?.ticket_type === 'free' ? 0 : addEditTicketForm?.values?.price,
      available_quantity: addEditTicketForm?.values.unlimited
        ? undefined
        : addEditTicketForm?.values?.available_quantity,
      unlimited: addEditTicketForm?.values?.unlimited,
      description: addEditTicketForm?.values?.description,
      ticket_type: addEditTicketForm?.values?.ticket_type,

      ticket_window_data: {
        start: {
          type: addEditTicketForm?.values?.ticket_window_data?.start?.type ?? 'on_publish_date',
          units:
            addEditTicketForm?.values?.ticket_window_data?.start?.type ===
            'x_time_before_event_date'
              ? 'days'
              : undefined,
          delta:
            addEditTicketForm?.values?.ticket_window_data?.start?.type ===
            'x_time_before_event_date'
              ? addEditTicketForm?.values?.ticket_window_data?.start?.delta
              : undefined,
          timestamp:
            addEditTicketForm?.values.ticket_window_data?.start?.type === 'on_event_date'
              ? addEditTicketForm?.values?.ticket_window_data?.start?.timestamp
              : undefined,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        end: {
          type:
            addEditTicketForm?.values?.ticket_window_data?.end?.type ===
            'one_hour_after_event_starts'
              ? 'x_time_before_event_date'
              : addEditTicketForm?.values?.ticket_window_data?.end?.type ?? 'on_event_date',
          units:
            addEditTicketForm?.values?.ticket_window_data?.end?.type ===
            'one_hour_after_event_starts'
              ? 'hours'
              : addEditTicketForm?.values?.ticket_window_data?.end?.type ===
                  'x_time_before_event_date'
                ? 'days'
                : undefined,
          delta:
            addEditTicketForm?.values?.ticket_window_data?.end?.type ===
            'one_hour_after_event_starts'
              ? -1
              : addEditTicketForm?.values?.ticket_window_data?.end?.type ===
                  'x_time_before_event_date'
                ? addEditTicketForm?.values?.ticket_window_data?.end?.delta
                : undefined,
          timestamp:
            addEditTicketForm?.values?.ticket_window_data?.end?.type === 'on_event_date'
              ? addEditTicketForm?.values?.ticket_window_data?.end?.timestamp
              : undefined,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
      options: options?.options,
    };

    if (isSeasonTicket) {
      if (addEditTicketForm?.values?.ticket_window_data?.start?.date) {
        Object.assign(ticket, {
          starting_at: addEditTicketForm?.values?.ticket_window_data?.start?.date.toISOString(),
        });
      }

      if (addEditTicketForm?.values?.ticket_window_data?.end?.date) {
        Object.assign(ticket, {
          expires_at: addEditTicketForm?.values?.ticket_window_data?.end?.date.toISOString(),
        });
      }
      Object.assign(ticket, {
        event_dates_ids: addEditTicketForm?.values?.included_event_dates,
      });
    }

    if (validateForms().isValid) {
      if (ticketId && !isDuplicate) {
        handleUpdate(ticket);
      } else {
        handleCreate(ticket);
      }
    }
  }, [
    addEditTicketForm?.values,
    handleCreate,
    handleUpdate,
    isDuplicate,
    isSeasonTicket,
    options?.options,
    setIsUniqueError,
    ticketId,
    validateForms,
  ]);

  return (
    <Modal.AffixedFooter>
      <Group justify={ticketId && !isDuplicate ? 'space-between' : 'flex-end'}>
        {ticketId && !isDuplicate && !campaignSettings.isCampaignPublished ? (
          <ButtonMantine
            size="md"
            variant="subtle"
            color="cerise"
            onClick={() => setIsDeleteModalOpen(true)}
          >
            Delete
          </ButtonMantine>
        ) : (
          <Box />
        )}
        <Group justify="flex-end">
          <ButtonMantine size="md" variant="subtle" onClick={onClose}>
            Cancel
          </ButtonMantine>
          <ButtonMantine
            w={160}
            size="md"
            onClick={handleSave}
            loading={isCreateTicketLoading || isUpdateTicketLoading}
          >
            {`Save${ticketId && !isDuplicate ? ' Changes' : ''}`}
          </ButtonMantine>
        </Group>
      </Group>

      {!ticketId ? null : (
        <DeleteTicketModal
          opened={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onSuccess={() => {
            onClose();
          }}
          ticketId={ticketId}
          causeId={causeId}
          campaignId={storeItemId}
        />
      )}
    </Modal.AffixedFooter>
  );
};

const AddEditTicketModal = ({
  opened,
  onClose,
  ticketId,
  isDuplicate = false,
  storeItemId,
  causeId,
  isSeasonTicket = false,
}: {
  opened: boolean;
  onClose: () => void;
  ticketId?: string;
  isDuplicate?: boolean;
  storeItemId?: string;
  causeId?: string;
  isSeasonTicket?: boolean;
}) => {
  const [isUniqueError, setIsUniqueError] = useState(false);

  const { data: campaignSettings } = useShowCampaignSettings({
    campaignId: storeItemId,
    causeId,
  });

  const { isCampaignReadOnly } = campaignSettings;

  const ticketType = useMemo(() => {
    if (isSeasonTicket) {
      return 'Season Ticket';
    } else {
      return 'Ticket Type';
    }
  }, [isSeasonTicket]);

  const modalTitle = useMemo(() => {
    if (isCampaignReadOnly) {
      return `View ${ticketType}`;
    }

    if (ticketId) {
      if (isDuplicate) {
        return `Duplicate ${ticketType}`;
      } else {
        return `Edit ${ticketType}`;
      }
    } else {
      return `Add ${ticketType}`;
    }
  }, [isCampaignReadOnly, isDuplicate, ticketId, ticketType]);

  const handleClose = useCallback(() => {
    setIsUniqueError(false);
    onClose();
  }, [onClose]);

  if (!causeId || !storeItemId) return null;

  return (
    <Modal size="lg" opened={opened} onClose={handleClose} title={modalTitle}>
      <Alert.Collapse in={isUniqueError} type="error" title={`${ticketType} already exists`}>
        It seems you&apos;re trying to create a {ticketType} that already exists. The name must be
        unique and cannot match an existing season or event ticket.
      </Alert.Collapse>
      <AddEditTicketContent
        ticketId={ticketId}
        storeItemId={storeItemId}
        causeId={causeId}
        isDuplicate={isDuplicate}
        isSeasonTicket={isSeasonTicket}
      />

      {!isCampaignReadOnly && (
        <AddEditTicketModalFooter
          ticketId={ticketId}
          onClose={handleClose}
          setIsUniqueError={setIsUniqueError}
          isDuplicate={isDuplicate}
          causeId={causeId}
          storeItemId={storeItemId}
          isSeasonTicket={isSeasonTicket}
        />
      )}
    </Modal>
  );
};

export { AddEditTicketModal };
